import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { QrCode, CurrencyInr, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * GenerateQrDialog — standalone UPI QR generator.
 * Not tied to any order or customer. Owner picks bank account (mode)
 * + amount + optional note → live QR renders. Useful for ad-hoc
 * collections from walk-in customers or over the phone.
 */
export default function GenerateQrDialog({ open, onClose }) {
  const [modes, setModes] = useState([]);
  const [modeKey, setModeKey] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/settings/payment-modes");
        const upiEnabled = (data || []).filter((m) => (m.upi_id || "").trim());
        setModes(upiEnabled);
        if (upiEnabled.length) setModeKey(upiEnabled[0].key);
      } catch (_e) {
        toast.error("Payment modes load nahi ho paye");
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) { setAmount(""); setNote(""); }
  }, [open]);

  const selected = useMemo(
    () => modes.find((m) => m.key === modeKey) || null,
    [modes, modeKey]
  );

  const amt = Number(amount) || 0;
  const qrSrc = selected && amt > 0
    ? `${BACKEND_BASE}/api/settings/payment-modes/${selected.key}/qr?amount=${encodeURIComponent(amt)}&note=${encodeURIComponent(note || "")}&t=${Date.now()}`
    : null;

  const download = () => {
    if (!qrSrc) return;
    const a = document.createElement("a");
    a.href = qrSrc;
    a.download = `QR-${selected.key}-${amt}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm"
        data-testid="generate-qr-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <QrCode size={20} className="text-emerald-400" weight="fill" />
            Generate QR
          </DialogTitle>
        </DialogHeader>

        {modes.length === 0 ? (
          <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
            <div className="font-heading tracking-widest text-amber-300 mb-1">NO UPI ID CONFIGURED</div>
            <div className="text-slate-300 text-xs leading-relaxed">
              QR banane ke liye <b>Settings → Payment Methods</b> mein jaake kisi bhi Bank Account ki
              <b> UPI ID</b> field bharo pehle.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  placeholder="0"
                  className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg mt-1.5"
                  data-testid="generate-qr-amount"
                />
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">RECEIVE INTO</Label>
                <Select value={modeKey} onValueChange={setModeKey}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1.5" data-testid="generate-qr-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {modes.map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {m.label} · {m.upi_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (OPTIONAL)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g., Advance for Gate #12"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                data-testid="generate-qr-note"
              />
            </div>

            {amt > 0 && selected ? (
              <div className="rounded-sm border border-emerald-500/40 bg-slate-950 p-3" data-testid="generate-qr-panel">
                <div className="bg-white rounded-sm p-3 flex flex-col items-center gap-2">
                  <img
                    src={qrSrc}
                    alt={`UPI QR for ₹${amt}`}
                    className="w-56 h-56"
                    data-testid="generate-qr-image"
                  />
                  <div className="text-center">
                    <div className="font-heading text-2xl font-black text-slate-900">
                      ₹{amt.toLocaleString("en-IN")}
                    </div>
                    <div className="text-[10px] font-mono-num tracking-widest text-slate-600 mt-0.5">
                      {selected.upi_id} · {selected.account_holder_name || selected.label}
                    </div>
                    <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mt-1">
                      SCAN & PAY — ANY UPI APP
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={download}
                  variant="outline"
                  className="w-full mt-3 rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800 h-10 font-heading tracking-widest"
                  data-testid="generate-qr-download"
                >
                  <Download size={14} className="mr-2" weight="bold" />
                  DOWNLOAD & SHARE
                </Button>
                <div className="text-[10px] font-mono-num tracking-widest text-emerald-300 mt-2 text-center">
                  ✓ Amount LOCKED — customer kam nahi kar sakta
                </div>
              </div>
            ) : (
              <div className="rounded-sm border border-slate-700 bg-slate-800/40 p-4 text-sm text-slate-400 text-center">
                <CurrencyInr size={24} weight="bold" className="mx-auto mb-2 text-slate-500" />
                Amount daalo — QR generate ho jayega.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="generate-qr-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
